import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material';
import { Subscription } from 'rxjs';
import { EquipmentService } from '@app/services/equipment.service';
import { Equipment, RecentMakeModelSearch } from '@app/model/equipment';
import { AddEquipmentDialogComponent } from '../add-equipment-dialog/add-equipment-dialog.component';
import { EquipmentDetailDialogComponent } from '../equipment-detail-dialog/equipment-detail-dialog.component';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-my-equipment',
  templateUrl: './my-equipment.component.html',
  styleUrls: ['./my-equipment.component.scss']
})
export class MyEquipmentComponent implements OnInit, OnDestroy {
  equipmentList: Equipment[] = [];
  isLoading = true;
  isListLayout = false;
  page = 1;
  pageSize = 12;
  total = 0;
  pageSizeOptions = [12, 24, 36];
  private subs: Subscription[] = [];

  constructor(
    private equipmentService: EquipmentService,
    private dialog: MatDialog,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private layoutUtilsService: LayoutUtilsService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.loadEquipment();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  loadEquipment(): void {
    this.isLoading = true;
    const sub = this.equipmentService.getEquipmentList(this.page, this.pageSize).subscribe(result => {
      this.equipmentList = result.equipment;
      this.total = result.total;
      this.isLoading = false;
      this.cdr.detectChanges();
    });
    this.subs.push(sub);
  }

  onPageChange(pageNumber: number): void {
    if (typeof pageNumber === 'number') {
      this.page = pageNumber;
      this.loadEquipment();
      window.scrollTo(0, 0);
    }
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.page = 1;
    this.loadEquipment();
  }


  openAddEquipmentDialog(prefill?: { make: string; model: string }): void {
    const dialogRef = this.dialog.open(AddEquipmentDialogComponent, {
      width: '95vw',
      maxWidth: '1100px',
      maxHeight: '90vh',
      panelClass: 'add-equipment-dialog',
      data: prefill || null
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Optimistic update: add or replace the equipment immediately in the list
        const idx = this.equipmentList.findIndex(e => e.id === result.id);
        if (idx > -1) {
          this.equipmentList[idx] = result;
        } else {
          this.equipmentList.unshift(result);
        }
        this.cdr.detectChanges();
        this.loadEquipment();
      }
    });
  }

  openEquipmentDetail(equipment: Equipment): void {
    const dialogRef = this.dialog.open(EquipmentDetailDialogComponent, {
      width: '95vw',
      maxWidth: '900px',
      maxHeight: '90vh',
      panelClass: 'equipment-detail-dialog',
      data: equipment
    });

    dialogRef.afterClosed().subscribe(result => {
      // Always reload list to ensure data is up-to-date (admin may have edited)
      this.loadEquipment();
    });
  }

  removeEquipment(equipment: Equipment, event: Event): void {
    event.stopPropagation();
    const title = 'Xóa thiết bị';
    const description = `Bạn có chắc chắn muốn xóa thiết bị "${equipment.make} ${equipment.model}" không?`;
    const waitDesciption = 'Đang xử lý...';

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDesciption);
    dialogRef.afterClosed().subscribe(res => {
      if (!res) {
        return;
      }
      this.isLoading = true;
      this.cdr.detectChanges();
      const sub = this.equipmentService.removeEquipment(equipment.id).subscribe(() => {
        this.loadEquipment();
      });
      this.subs.push(sub);
    });
  }


  uploadImage(equipment: Equipment, event: Event): void {
    event.stopPropagation();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      const sub = this.equipmentService.setDefaultImage(equipment.id, file).subscribe(
        (res) => {
          equipment.imageUrl = res.default_image || res.imageUrl || res.url || equipment.imageUrl;
          this.cdr.detectChanges();
          this.toastr.success('Đã cập nhật ảnh thành công', 'Thành công');
        },
        (err) => {
          console.error('Lỗi tải ảnh:', err);
          this.toastr.error('Không thể tải ảnh lên', 'Lỗi');
        }
      );
      this.subs.push(sub);
    };
    input.click();
  }

  getEquipmentImage(equipment: Equipment): string {
    const path = equipment.imageUrl;
    if (!path) return '/assets/media/icons/svg/forklift-placeholder.svg';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('assets/')) {
      return path;
    }
    const host = environment.backendhost || '';
    const cleanHost = host.endsWith('/') ? host.slice(0, -1) : host;
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    return cleanHost + cleanPath;
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('vi-VN');
  }

  toggleView(isList: boolean): void {
    this.isListLayout = isList;
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Ngừng hoạt động';
      case 'maintenance':
        return 'Bảo trì';
      case 'retired':
        return 'Ngừng sử dụng';
      default:
        return 'Đang hoạt động';
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active':
        return 'status-badge-active';
      case 'inactive':
        return 'status-badge-inactive';
      case 'maintenance':
        return 'status-badge-maintenance';
      case 'retired':
        return 'status-badge-retired';
      default:
        return 'status-badge-active';
    }
  }
}
